/** Matches Supabase "Lowercase, uppercase letters, digits and symbols (recommended)" policy. */

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 72;

/** Allowed symbols per Supabase Auth docs */
export const PASSWORD_SYMBOLS = "!@#$%^&*()_+-=[]{};':\"|<>?,./`~";

/** Every character in the password must be from this set (Supabase rejects others). */
export const ALLOWED_PASSWORD_CHARS =
  "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789" +
  PASSWORD_SYMBOLS;

const LOWERCASE = /[a-z]/;
const UPPERCASE = /[A-Z]/;
const DIGIT = /[0-9]/;
const SYMBOL = new RegExp(
  `[${PASSWORD_SYMBOLS.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}]`,
);
const ALLOWED_PASSWORD_CHAR_RE = new RegExp(
  `^[${ALLOWED_PASSWORD_CHARS.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}]+$`,
);

export type PasswordRequirementId =
  | "length"
  | "lowercase"
  | "uppercase"
  | "digit"
  | "symbol"
  | "allowedChars";

export interface PasswordRequirement {
  id: PasswordRequirementId;
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: "length",
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    test: (p) => p.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: "lowercase",
    label: "One lowercase letter (a–z)",
    test: (p) => LOWERCASE.test(p),
  },
  {
    id: "uppercase",
    label: "One uppercase letter (A–Z)",
    test: (p) => UPPERCASE.test(p),
  },
  {
    id: "digit",
    label: "One number (0–9)",
    test: (p) => DIGIT.test(p),
  },
  {
    id: "symbol",
    label: "One symbol (!@#$%…)",
    test: (p) => SYMBOL.test(p),
  },
  {
    id: "allowedChars",
    label: "Only allowed letters, numbers, and symbols (no spaces)",
    test: (p) => p.length === 0 || ALLOWED_PASSWORD_CHAR_RE.test(p),
  },
];

export type PasswordStrengthLabel =
  | "Very weak"
  | "Weak"
  | "Fair"
  | "Good"
  | "Strong";

export interface PasswordStrength {
  score: number;
  label: PasswordStrengthLabel;
  color: string;
  metCount: number;
  totalCount: number;
}

export function getPasswordStrength(password: string): PasswordStrength {
  if (!password) {
    return {
      score: 0,
      label: "Very weak",
      color: "red.400",
      metCount: 0,
      totalCount: PASSWORD_REQUIREMENTS.length,
    };
  }

  const metCount = PASSWORD_REQUIREMENTS.filter((r) => r.test(password)).length;
  const totalCount = PASSWORD_REQUIREMENTS.length;
  const score = Math.round((metCount / totalCount) * 100);

  let label: PasswordStrengthLabel;
  let color: string;

  if (score === 100) {
    label = "Strong";
    color = "green.500";
  } else if (score >= 80) {
    label = "Good";
    color = "teal.500";
  } else if (score >= 60) {
    label = "Fair";
    color = "yellow.500";
  } else if (score >= 40) {
    label = "Weak";
    color = "orange.400";
  } else {
    label = "Very weak";
    color = "red.400";
  }

  return { score, label, color, metCount, totalCount };
}

export function validatePassword(password: string): {
  valid: boolean;
  message?: string;
  failedRequirement?: PasswordRequirementId;
} {
  if (password.length > PASSWORD_MAX_LENGTH) {
    return {
      valid: false,
      message: `Password cannot be longer than ${PASSWORD_MAX_LENGTH} characters`,
      failedRequirement: "length",
    };
  }

  for (const req of PASSWORD_REQUIREMENTS) {
    if (!req.test(password)) {
      if (req.id === "allowedChars") {
        const invalid = [...password].find(
          (ch) => !ALLOWED_PASSWORD_CHARS.includes(ch),
        );
        const hint = invalid
          ? ` Remove "${invalid === " " ? "space" : invalid}".`
          : "";
        return {
          valid: false,
          message: `Password contains characters that are not allowed.${hint} Use only letters, numbers, and symbols shown in the requirements.`,
          failedRequirement: req.id,
        };
      }
      return {
        valid: false,
        message: `Password must meet all requirements. Missing: ${req.label.toLowerCase()}.`,
        failedRequirement: req.id,
      };
    }
  }

  return { valid: true };
}

type WeakPasswordMeta = {
  reasons: string[];
  message: string;
};

function extractWeakPasswordMeta(error: unknown): WeakPasswordMeta {
  const err = error as {
    message?: string;
    msg?: string;
    error_description?: string;
    reasons?: string[];
    weak_password?: { reasons?: string[]; message?: string };
    weakPassword?: { reasons?: string[]; message?: string };
  };

  const weak = err?.weak_password ?? err?.weakPassword;
  const reasons = [
    ...(weak?.reasons ?? []),
    ...(Array.isArray(err?.reasons) ? err.reasons : []),
  ];
  const message =
    weak?.message ||
    err?.message ||
    err?.msg ||
    err?.error_description ||
    "";

  return { reasons, message };
}

function messageFromWeakPasswordReasons(reasons: string[]): string {
  const parts: string[] = [];
  if (reasons.includes("length")) {
    parts.push(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
  }
  if (reasons.includes("characters")) {
    parts.push(
      "Password must include lowercase and uppercase letters, a number, and a symbol from the allowed set.",
    );
  }
  if (reasons.includes("pwned")) {
    parts.push(
      "This password is too common or has appeared in a data breach. Choose a more unique password (avoid patterns like Password1!).",
    );
  }
  return parts.join(" ");
}

export function mapSupabasePasswordError(error: unknown): string {
  const err = error as {
    code?: string;
    error_code?: string;
    name?: string;
    message?: string;
    msg?: string;
  };

  const { reasons, message } = extractWeakPasswordMeta(error);

  if (reasons.length) {
    const fromReasons = messageFromWeakPasswordReasons(reasons);
    if (fromReasons) return fromReasons;
  }

  if (message) {
    if (
      /pwned|known to be weak|easy to guess|compromised|data breach|leaked/i.test(
        message,
      )
    ) {
      return "This password is too common or has appeared in a data breach. Choose a more unique password (avoid patterns like Password1!).";
    }
    return message;
  }

  if (
    err?.code === "weak_password" ||
    err?.error_code === "weak_password" ||
    err?.name === "AuthWeakPasswordError"
  ) {
    return "Password was rejected by Supabase security rules. Use a unique password with mixed characters and no spaces.";
  }

  return "Password was rejected. Meet all requirements above and avoid common passwords (e.g. Password1!).";
}

export function isSupabaseWeakPasswordError(error: unknown): boolean {
  const err = error as {
    code?: string;
    error_code?: string;
    name?: string;
    message?: string;
    msg?: string;
    weak_password?: { reasons?: string[] };
    weakPassword?: { reasons?: string[] };
  };

  const { reasons, message } = extractWeakPasswordMeta(error);

  return (
    err?.code === "weak_password" ||
    err?.error_code === "weak_password" ||
    err?.name === "AuthWeakPasswordError" ||
    reasons.length > 0 ||
    /pwned|weak_password|known to be weak|easy to guess/i.test(
      message || err?.message || err?.msg || "",
    )
  );
}
